<script lang="ts">
  import { onDestroy, onMount, type Snippet } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import FormWrapper, { type FormContext } from "@kontextwerk/web-sdk/ui/layout/FormWrapper.svelte"
  import { validateField, validateInput } from "@kontextwerk/web-sdk/core/forms/formValidation"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import { mdiPlus } from "@mdi/js"
  import { ApiError } from "../../../../../../../../../../../../api/kehrblatt/client"
  import {
    createProperty,
    updateProperty,
  } from "../../../../../../../../../../../../api/kehrblatt/property-registry/properties"
  import {
    createPropertyCustomer,
    deletePropertyCustomer,
  } from "../../../../../../../../../../../../api/kehrblatt/property-customer-registry/property-customers"
  import {
    confirmSimilarAddress,
    normalizeAddressInput,
  } from "../../../../../../_shared/_logic/address-duplicate-guard"
  import {
    DEFAULT_COUNTRY,
    normalizeDachCountrySelection,
  } from "../../../../../../../../../../../../_shared/_logic/countries"
  import PropertyForm from "../../../../../../_shared/_modules/property-form"
  import PropertyUnitsTable, {
    type PropertyUnitRow,
  } from "../../../../../../_shared/_modules/property-units-table"
  import PropertyUnitOverlay, {
    type UnitFormModel,
  } from "../../../../../../_shared/_modules/property-unit-overlay"
  import {
    defaultOutreachEnabledForRole,
    normalizePropertyCustomerRole,
  } from "../../../../../../../../../../_shared/_logic/customer-roles"
  import {
    isBuildingPropertyUnit,
    normalizePropertyUnitType,
    type PropertyUnitType,
  } from "../../../../../../../../../../_shared/_logic/units"

  type OverlayHostProps = {
    onremoveOverlay?: () => void
    onshowOverlay?: () => void
    setActionButtonSnippet?: (snippet: Snippet | null) => void
  }

  type AddressPrefill = Partial<Pick<Property, "zip" | "city" | "country">>

  import type { AddressOverlayResult } from "./_logic/types"

  type Props = {
    organizationId: string
    customers: Customer[]
    defaultCustomerId?: string | null
    defaultAddress?: AddressPrefill | null
    editingLink?: PropertyCustomer | null
    editMode?: boolean
    property?: Property | null
    lightMode?: boolean
    onSaved?: (payload: AddressOverlayResult) => void
  }

  type AddressFormModel = {
    street: string
    house_number: string
    zip: string
    city: string
    country: string
    address_extra: string
    geo_lat: number | string | null
    geo_lng: number | string | null
    units: ApartmentFormUnit[]
  }

  type ApartmentFormUnit = {
    id?: string | null
    unit_label: string
    unit_number?: string | null
    notes?: string | null
    client_id?: string
    unit_type?: PropertyUnitType
    customer_ids?: string[]
    role?: PropertyCustomerRole
  }

  const DEFAULT_BUILDING_UNIT_LABEL = "Gebäudeeinheit"
  const fieldIds = {
    street: "address-street",
    houseNumber: "address-house-number",
    zip: "address-zip",
    city: "address-city",
    country: "address-country",
    addressExtra: "address-extra",
  }

  let {
    organizationId,
    customers = [],
    defaultCustomerId = null,
    defaultAddress = null,
    editingLink = null,
    editMode = !!editingLink,
    property = null,
    lightMode = false,
    onSaved = () => {},
    onremoveOverlay = null,
    onshowOverlay = null,
    setActionButtonSnippet = null,
  }: Props & OverlayHostProps = $props()

  let submitting = $state(false)
  let submitForm = $state<(() => Promise<void>) | null>(null)

  const createUnitClientId = () =>
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `unit-${Math.random().toString(16).slice(2)}`

  const unitKey = (unit: ApartmentFormUnit) =>
    unit.id ?? unit.client_id ?? `${unit.unit_label}-${unit.unit_number ?? ""}`

  const applyDefaultAddress = (form: AddressFormModel) => {
    const merged: AddressFormModel = { ...form }

    if (defaultAddress?.zip && !merged.zip) merged.zip = defaultAddress.zip

    if (defaultAddress?.city && !merged.city) merged.city = defaultAddress.city

    if (defaultAddress?.country && !merged.country) {
      merged.country = normalizeDachCountrySelection(defaultAddress.country)
    }

    if (!(merged.country ?? "").trim()) {
      merged.country = DEFAULT_COUNTRY
    } else {
      merged.country = normalizeDachCountrySelection(merged.country)
    }

    return merged
  }

  const uniqueIds = (ids: (string | null | undefined)[]) =>
    Array.from(new Set((ids ?? []).map((id) => (id ?? "").trim()).filter(Boolean))) as string[]

  const resolveDefaultLinkRole = (): PropertyCustomerRole => {
    return "OTHER"
  }

  const mapUnits = (units: Property["units"] | undefined | null): ApartmentFormUnit[] =>
    (units ?? []).map((unit) => ({
      id: unit.id,
      client_id: unit.id ?? createUnitClientId(),
      unit_label: unit.unit_label,
      unit_number: unit.unit_number ?? null,
      notes: unit.notes ?? null,
      unit_type: normalizePropertyUnitType(unit.unit_type),
      customer_ids: [],
      role: resolveDefaultLinkRole(),
    }))

  const buildInitObject = (): AddressFormModel => {
    const baseUnitsMapped = mapUnits(property?.units)
    const fallbackUnits: ApartmentFormUnit[] = [
      {
        id: editingLink?.property_unit_id ?? null,
        client_id: editingLink?.property_unit_id ?? createUnitClientId(),
        unit_label: DEFAULT_BUILDING_UNIT_LABEL,
        unit_number: null,
        notes: null,
        unit_type: "BUILDING",
        customer_ids: uniqueIds(
          editingLink ? [editingLink.customer_id] : defaultCustomerId ? [defaultCustomerId] : [],
        ),
        role: editingLink?.role ?? resolveDefaultLinkRole(),
      },
    ]
    const baseUnits = baseUnitsMapped.length > 0 ? baseUnitsMapped : fallbackUnits
    const base: AddressFormModel = applyDefaultAddress({
      street: property?.street ?? "",
      house_number: property?.house_number ?? "",
      zip: property?.zip ?? "",
      city: property?.city ?? "",
      country: property?.country ?? "",
      address_extra: property?.address_extra ?? "",
      geo_lat: property?.geo_lat ?? null,
      geo_lng: property?.geo_lng ?? null,
      units: baseUnits,
    })

    if (editingLink) {
      base.units = base.units.map((unit) => {
        if (unit.id && editingLink.property_unit_id && unit.id === editingLink.property_unit_id) {
          return {
            ...unit,
            customer_ids: uniqueIds([editingLink.customer_id, ...(unit.customer_ids ?? [])]),
            role: editingLink.role ?? unit.role ?? "OTHER",
          }
        }

        return unit
      })
    }

    return base
  }

  const parseNumberOrNull = (value: number | string | null): number | null => {
    if (value === null || value === "") return null

    const parsed = Number(value)

    return Number.isFinite(parsed) ? parsed : null
  }

  const normalizeAddressValue = (value: string | null | undefined) => (value ?? "").trim()

  const hasAddressChanges = (data: AddressFormModel, current?: Property | null) => {
    if (!current) return true

    return (
      normalizeAddressValue(data.street) !== normalizeAddressValue(current.street) ||
      normalizeAddressValue(data.house_number) !== normalizeAddressValue(current.house_number) ||
      normalizeAddressValue(data.zip) !== normalizeAddressValue(current.zip) ||
      normalizeAddressValue(data.city) !== normalizeAddressValue(current.city) ||
      normalizeAddressValue(data.country) !== normalizeAddressValue(current.country) ||
      normalizeAddressValue(data.address_extra) !== normalizeAddressValue(current.address_extra) ||
      parseNumberOrNull(data.geo_lat) !== (current.geo_lat ?? null) ||
      parseNumberOrNull(data.geo_lng) !== (current.geo_lng ?? null)
    )
  }

  const normalizeUnitForCompare = (unit: ApartmentFormUnit) => ({
    id: unit.id ?? null,
    label: unit.unit_label.trim(),
    number: (unit.unit_number ?? "").trim(),
    notes: unit.notes ?? null,
    unit_type: normalizePropertyUnitType(unit.unit_type),
  })

  const haveUnitsChanged = (
    dataUnits: ApartmentFormUnit[],
    currentUnits?: Property["units"] | null,
  ) => {
    if (!currentUnits) return true

    const nextList = dataUnits
      .map(normalizeUnitForCompare)
      .sort((a, b) => (a.id ?? a.label).localeCompare(b.id ?? b.label))
    const currentList = (currentUnits ?? [])
      .map((unit) =>
        normalizeUnitForCompare({
          id: unit.id,
          unit_label: unit.unit_label,
          unit_number: unit.unit_number,
          notes: unit.notes,
          unit_type: normalizePropertyUnitType(unit.unit_type),
        }),
      )
      .sort((a, b) => (a.id ?? a.label).localeCompare(b.id ?? b.label))

    if (nextList.length !== currentList.length) return true

    return nextList.some((entry, idx) => {
      const current = currentList[idx]

      return (
        entry.id !== current.id ||
        entry.label !== current.label ||
        entry.unit_type !== current.unit_type ||
        (entry.notes ?? null) !== (current.notes ?? null)
      )
    })
  }

  const resolveUnitId = (sourceUnit: ApartmentFormUnit, targetProperty: Property | null) => {
    const units = targetProperty?.units ?? []

    if (!units.length) return null

    if (sourceUnit.id) {
      const match = units.find((unit) => unit.id === sourceUnit.id)

      if (match) return match.id ?? null
    }

    const match = units.find(
      (unit) =>
        unit.unit_label === sourceUnit.unit_label &&
        (unit.unit_number ?? null) === (sourceUnit.unit_number ?? null) &&
        (unit.notes ?? null) === (sourceUnit.notes ?? null),
    )

    return match?.id ?? null
  }

  const buildTargetLinks = (data: AddressFormModel, targetProperty: Property | null) => {
    const links: PropertyCustomerCreatePayload[] = []
    for (const unit of data.units) {
      const customerIds = uniqueIds(unit.customer_ids ?? [])

      if (!customerIds.length) continue

      const targetUnitId = resolveUnitId(unit, targetProperty)

      if (!targetUnitId) {
        throw new Error("Einheit konnte nicht zugeordnet werden.")
      }

      const role = normalizePropertyCustomerRole(unit.role ?? "OTHER")
      customerIds.forEach((customerId) => {
        links.push({
          property_id: targetProperty?.id ?? "",
          property_unit_id: targetUnitId,
          customer_id: customerId,
          role,
          outreach_enabled: defaultOutreachEnabledForRole(role),
          is_primary: false,
        })
      })
    }

    return links
  }

  const validateAddress = ({ data }: { data: AddressFormModel; form: HTMLFormElement | null }) => {
    let isValid = true

    if (!editingLink) {
      if (!validateField(fieldIds.street, data.street, validateInput)) isValid = false
    }

    if (!data.units.length) {
      newNotification({ class: "error", html: "Bitte mindestens eine Einheit anlegen." })
      isValid = false
    }

    const buildingUnits = data.units.filter(isBuildingPropertyUnit)

    if (buildingUnits.length !== 1) {
      newNotification({ class: "error", html: "Bitte genau eine Gebäudeeinheit anlegen." })
      isValid = false
    }

    return isValid
  }

  const saveAddress = async (data: AddressFormModel) => {
    if (!organizationId) return

    const normalizedAddress = normalizeAddressInput({
      street: data.street,
      house_number: data.house_number,
      zip: data.zip,
      city: data.city,
      country: data.country,
    })
    const street = normalizedAddress.street
    const city = normalizedAddress.city
    const zip = normalizedAddress.zip
    const houseNumber = normalizedAddress.house_number
    const country = normalizedAddress.country
    const persistedProperty = property?.id ? property : null
    const unitsLoaded = Array.isArray(persistedProperty?.units)
    const unitsChanged = !persistedProperty
      ? true
      : unitsLoaded
        ? haveUnitsChanged(data.units, persistedProperty?.units ?? null)
        : false
    const shouldSyncUnits = !persistedProperty
      ? true
      : editingLink
        ? unitsChanged && unitsLoaded
        : unitsChanged
    const normalizedData: AddressFormModel = {
      ...data,
      ...normalizedAddress,
      house_number: houseNumber,
    }
    const addressChanged = hasAddressChanges(normalizedData, persistedProperty)

    if (!street && !editingLink) {
      newNotification({ class: "error", html: "Bitte eine Adresse angeben." })

      return
    }

    const shouldCheckDuplicates = !persistedProperty || addressChanged

    if (shouldCheckDuplicates) {
      const approved = await confirmSimilarAddress({
        organizationId,
        address: normalizedAddress,
        excludePropertyId: persistedProperty?.id ?? null,
        approvalLabel: persistedProperty ? "Trotzdem ändern" : "Trotzdem anlegen",
      })

      if (!approved) return
    }

    const normalizedUnits = (data.units.length ? data.units : []).map((unit) => ({
      id: unit.id,
      unit_label: unit.unit_label.trim(),
      unit_number: unit.unit_number?.trim() || null,
      notes: unit.notes ?? null,
      unit_type: normalizePropertyUnitType(unit.unit_type),
    }))

    const payload: PropertyCreatePayload = {
      street: street || (editingLink ? property?.street || "—" : "—"),
      house_number: houseNumber || null,
      zip: zip || null,
      city: city || null,
      country: country || null,
      address_extra: data.address_extra.trim() || null,
      organization_id: organizationId,
      geo_lat: parseNumberOrNull(data.geo_lat),
      geo_lng: parseNumberOrNull(data.geo_lng),
      ...(shouldSyncUnits && { units: normalizedUnits }),
    }

    try {
      let targetProperty: Property | null = persistedProperty
      const needsUpdate = !persistedProperty || addressChanged || unitsChanged

      if (needsUpdate) {
        const propertyIdToUpdate = editingLink?.property_id ?? (persistedProperty?.id || null)

        if (propertyIdToUpdate) {
          const { organization_id: _orgId, ...updatePayload } = payload
          targetProperty = await updateProperty(propertyIdToUpdate, updatePayload)
        } else {
          targetProperty = await createProperty(payload)
        }
      }

      if (!targetProperty) {
        throw new Error("Liegenschaft konnte nicht geladen werden.")
      }

      let removedLinkId: string | null = null
      const targetLinks = buildTargetLinks(data, targetProperty)
      const createdLinks: PropertyCustomer[] = []

      if (editingLink) {
        const stillRequested = targetLinks.some(
          (link) =>
            link.customer_id === editingLink.customer_id &&
            link.property_unit_id === editingLink.property_unit_id &&
            link.role === editingLink.role,
        )

        if (!stillRequested) {
          await deletePropertyCustomer(editingLink.id)
          removedLinkId = editingLink.id
        }
      }

      for (const linkPayload of targetLinks) {
        const link = await createPropertyCustomer(linkPayload)
        createdLinks.push(link)
      }

      onSaved?.({
        property: targetProperty,
        link: createdLinks[0] ?? null,
        removedLinkId,
      })
      const wasUpdate = Boolean(editingLink || persistedProperty?.id)
      newNotification({
        class: "success",
        html: wasUpdate ? "Liegenschaft wurde aktualisiert." : "Liegenschaft wurde angelegt.",
      })
      onremoveOverlay?.()
    } catch (err) {
      console.error("address_save_failed", err)
      const message =
        (err instanceof ApiError && err.message) ||
        (err instanceof Error && err.message) ||
        "Liegenschaft konnte nicht gespeichert werden."
      newNotification({ class: "error", html: message })
    }
  }

  const normalizeUnit = (unit: UnitFormModel): ApartmentFormUnit => ({
    id: unit.id,
    client_id: unit.client_id ?? unit.id ?? createUnitClientId(),
    unit_label: unit.unit_label.trim(),
    unit_number: unit.unit_number?.trim() || null,
    notes: unit.notes ?? null,
    unit_type: normalizePropertyUnitType(unit.unit_type),
    customer_ids: uniqueIds(unit.customer_ids ?? []),
    role: unit.role ?? "OTHER",
  })

  const syncUnits = (
    units: ApartmentFormUnit[],
    data: AddressFormModel,
    updateData: (next: Partial<AddressFormModel>) => void,
  ) => {
    updateData({ units })
  }

  const upsertUnit = (
    unit: UnitFormModel,
    data: AddressFormModel,
    updateData: (next: Partial<AddressFormModel>) => void,
  ) => {
    const normalized = normalizeUnit(unit)
    const targetKey = unitKey(normalized)
    const exists = data.units.some((entry) => unitKey(entry) === targetKey)
    const nextUnits = exists
      ? data.units.map((entry) => (unitKey(entry) === targetKey ? normalized : entry))
      : [...data.units, normalized]
    syncUnits(nextUnits, data, updateData)
  }

  const addApartment = (
    data: AddressFormModel,
    updateData: (next: Partial<AddressFormModel>) => void,
  ) => {
    const allowTypeEdit = !property?.id && !editingLink
    openOverlay({
      title: "Einheit hinzufügen",
      content: PropertyUnitOverlay,
      isDark: true,
      properties: {
        unit: null,
        organizationId,
        customers,
        customerIds: defaultCustomerId ? [defaultCustomerId] : [],
        defaultRole: resolveDefaultLinkRole(),
        darkMode: true,
        allowTypeEdit,
        onSaved: (unit: UnitFormModel) => upsertUnit(unit, data, updateData),
      },
    })
  }

  const editApartment = (
    unit: ApartmentFormUnit,
    data: AddressFormModel,
    updateData: (next: Partial<AddressFormModel>) => void,
  ) => {
    const allowTypeEdit = !property?.id && !editingLink
    openOverlay({
      title: "Einheit bearbeiten",
      content: PropertyUnitOverlay,
      isDark: true,
      properties: {
        unit,
        organizationId,
        customers,
        defaultRole: resolveDefaultLinkRole(),
        darkMode: true,
        allowTypeEdit,
        onSaved: (nextUnit: UnitFormModel) => upsertUnit(nextUnit, data, updateData),
      },
    })
  }

  const deleteApartment = (
    unit: ApartmentFormUnit,
    data: AddressFormModel,
    updateData: (next: Partial<AddressFormModel>) => void,
  ) => {
    if (isBuildingPropertyUnit(unit)) return

    const targetKey = unitKey(unit)
    syncUnits(
      data.units.filter((entry) => unitKey(entry) !== targetKey),
      data,
      updateData,
    )
  }

  const closeOverlay = (submitting: boolean) => {
    if (submitting) return

    onremoveOverlay?.()
  }

  const reinitKey = $derived(editingLink?.id ?? property?.id ?? defaultCustomerId ?? "new")

  onMount(() => {
    onshowOverlay?.()
    setActionButtonSnippet?.(actionButtons)
  })

  onDestroy(() => {
    setActionButtonSnippet?.(null)
  })
</script>

<div class="overlay-form">
  <FormWrapper
    theme={lightMode ? "dark" : "light"}
    mode={editMode ? "edit" : "create"}
    getInitObject={buildInitObject}
    validateData={validateAddress}
    onSubmit={saveAddress}
    submitLabel={editMode ? "Speichern" : "Anlegen"}
    showReset={false}
    {reinitKey}
    formSnippet={formContent}
    bind:submitting
    setSubmitFn={(fn) => (submitForm = fn)}
  />
</div>

{#snippet formContent(ctx: FormContext)}
  {@const { data, updateData, submitting } = ctx}
  <PropertyForm
    {data}
    {updateData}
    disabled={submitting}
    fieldIds={{
      street: fieldIds.street,
      house_number: fieldIds.houseNumber,
      zip: fieldIds.zip,
      city: fieldIds.city,
      country: fieldIds.country,
      address_extra: fieldIds.addressExtra,
    }}
  />

  <section class="apartment-section">
    <PropertyUnitsTable
      units={data.units as PropertyUnitRow[]}
      showCustomers={false}
      showActions={true}
      onEdit={(unit) => editApartment(unit as ApartmentFormUnit, data, updateData)}
      onArchive={(unit) => deleteApartment(unit as ApartmentFormUnit, data, updateData)}
      disabled={submitting}
      theme={lightMode ? "light" : "dark"}
    />
    <CTA
      type="secondaryButton"
      content=""
      tooltipContent="Einheit hinzufügen"
      onClick={() => addApartment(data, updateData)}
      disabled={submitting}
    >
      {#snippet iconSnippet()}
        <Icon path={mdiPlus} />
      {/snippet}
    </CTA>
  </section>
{/snippet}

{#snippet actionButtons()}
  <CTA
    type="secondaryButton"
    content="Abbrechen"
    onClick={() => closeOverlay(submitting)}
    disabled={submitting}
  />
  <CTA
    type="primaryButton"
    content={submitting ? "Speichere…" : editMode ? "Speichern" : "Anlegen"}
    onClick={() => submitForm?.()}
    disabled={submitting}
  />
{/snippet}

<style lang="less">
  @import "@kontextwerk/web-sdk/theme/tokens.less";

  .overlay-form {
    display: flex;
    flex-direction: column;
    height: fit-content;
    gap: 0.85rem;
    padding-bottom: 0.5rem;
  }

  .apartment-section {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1.2rem;
  }

  :global(.overlay-form .formActions) {
    display: none;
  }
</style>
